import { useState, useEffect } from "react";

const sections = [
  {
    id: 1,
    title: "Introduction",
    content: `Welcome to Causaler. These Terms of Service govern your use of our website and services.

Causaler provides digital services including website design, development, branding, and digital presence solutions for small businesses.

By engaging with our services, you agree to these terms.`,
  },
  {
    id: 2,
    title: "Services We Provide",
    content: `Causaler offers:

• Website design & development  
• Landing page creation  
• Business portfolio websites  
• Website optimization  
• Basic digital presence consultation  

All services are delivered as per agreed scope before project initiation.`,
  },
  {
    id: 3,
    title: "Client Responsibilities",
    content: `Clients must:

• Provide accurate business information  
• Share required content (logo, text, images) on time  
• Respond to revision requests promptly  
• Ensure they have rights to all provided materials  

Delays in communication may impact delivery timelines.`,
  },
  {
    id: 4,
    title: "Payments & Pricing",
    content: `All pricing is discussed and finalized before project start.

• Advance payment may be required  
• Remaining payment due before final delivery  
• Payments once made are non-refundable unless agreed  

Custom packages may vary based on project scope.`,
  },
  {
    id: 5,
    title: "Intellectual Property",
    content: `After full payment:

• Clients own the final website delivered  
• Causaler reserves right to showcase work in portfolio  

Third-party assets (fonts, plugins, hosting tools) remain property of their respective owners.`,
  },
  {
    id: 6,
    title: "Revisions Policy",
    content: `Revisions are limited to the agreed package.

Additional major changes outside scope may incur additional charges.`,
  },
  {
    id: 7,
    title: "Limitation of Liability",
    content: `Causaler is not liable for:

• Business losses  
• Third-party service failures  
• Hosting downtime  
• Client content accuracy  

We aim for best quality but cannot guarantee business results.`,
  },
  {
    id: 8,
    title: "Termination",
    content: `Either party may terminate services before completion.

Work completed up to that point will be chargeable.`,
  },
  {
    id: 9,
    title: "Contact Us",
    content: `For any queries regarding these Terms:

Email: contact@causaler.com  
Website: https://causaler.com`,
  },
];

export default function TermsOfService() {
  const [activeTab, setActiveTab] = useState(1);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const styles = {
    page: {
      background: "linear-gradient(135deg, #0b1d3a 0%, #112b5c 100%)",
      minHeight: "100vh",
      padding: "60px 20px",
    },
    container: {
      maxWidth: "1200px",
      margin: "0 auto",
      backgroundColor: "#ffffff",
      borderRadius: "20px",
      padding: isMobile ? "25px" : "40px",
      boxShadow: "0 20px 50px rgba(0,0,0,0.15)",
    },
    title: {
      fontSize: "32px",
      fontWeight: "700",
      textAlign: "center",
      marginBottom: "10px",
      color: "#0b1d3a",
      fontFamily: "'Science Gothic', sans-serif",
    },
    updated: {
      textAlign: "center",
      color: "#777",
      marginBottom: "40px",
      fontSize: "14px",
      fontFamily:
        "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    contentWrapper: {
      display: isMobile ? "block" : "flex",
      gap: "30px",
      alignItems: "flex-start",
    },
    sidebar: {
      flex: "0 0 260px",
      maxHeight: "75vh",
      overflowY: "auto",
      position: "sticky",
      top: "20px",
      paddingRight: "8px",
      display: isMobile ? "none" : "flex",
      flexDirection: "column",
      gap: "12px",
    },
    tab: (active) => ({
      padding: "14px 16px",
      borderRadius: "10px",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: "500",
      transition: "all 0.3s ease",
      fontFamily: "'Science Gothic', sans-serif",
      backgroundColor: active ? "#ff7a00" : "#0b1d3a",
      color: "#fff",
    }),
    contentArea: {
      flex: "1",
      backgroundColor: "#f8fafc",
      padding: "30px",
      borderRadius: "14px",
      minHeight: isMobile ? "auto" : "75vh",
    },
    sectionTitle: {
      fontSize: "22px",
      fontWeight: "600",
      marginBottom: "15px",
      color: "#0b1d3a",
      fontFamily: "'Science Gothic', sans-serif",
    },
    text: {
      fontSize: "15px",
      lineHeight: "1.8",
      color: "#444",
      whiteSpace: "pre-line",
      fontFamily:
        "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    mobileSection: {
      marginBottom: "12px",
    },
    mobileTab: (active) => ({
      padding: "14px",
      borderRadius: "10px",
      fontWeight: "500",
      cursor: "pointer",
      backgroundColor: active ? "#ff7a00" : "#0b1d3a",
      color: "#fff",
      fontFamily: "'Science Gothic', sans-serif",
    }),
    mobileContent: {
      backgroundColor: "#f8fafc",
      padding: "20px",
      borderRadius: "10px",
      marginTop: "8px",
    },
    footer: {
      marginTop: "50px",
      textAlign: "center",
      fontSize: "14px",
      color: "#666",
      fontFamily:
        "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <h1 style={styles.title}>Terms of Service</h1>
        <p style={styles.updated}>Last updated: February 2026</p>

        <div style={styles.contentWrapper}>
          {/* Desktop Sidebar */}
          <div style={styles.sidebar} className="sidebar">
            {sections.map((section) => (
              <div
                key={section.id}
                style={styles.tab(activeTab === section.id)}
                onClick={() => setActiveTab(section.id)}
              >
                {section.title}
              </div>
            ))}
          </div>

          {/* Desktop Content */}
          {!isMobile && (
            <div style={styles.contentArea}>
              <h2 style={styles.sectionTitle}>
                {sections.find((s) => s.id === activeTab).title}
              </h2>
              <p style={styles.text}>
                {sections.find((s) => s.id === activeTab).content}
              </p>
            </div>
          )}

          {/* Mobile Accordion */}
          {isMobile &&
            sections.map((section) => (
              <div key={section.id} style={styles.mobileSection}>
                <div
                  style={styles.mobileTab(activeTab === section.id)}
                  onClick={() =>
                    setActiveTab(
                      activeTab === section.id ? null : section.id
                    )
                  }
                >
                  {section.title}
                </div>
                {activeTab === section.id && (
                  <div style={styles.mobileContent}>
                    <h3 style={styles.sectionTitle}>{section.title}</h3>
                    <p style={styles.text}>{section.content}</p>
                  </div>
                )}
              </div>
            ))}
        </div>

        <div style={styles.footer}>
          © 2026 Causaler. All rights reserved.
        </div>
      </div>
    </div>
  );
}